import { Injectable, NgZone } from "@angular/core";
import { fromEvent, Subscription, throttleTime } from "rxjs";

// Enum for scroll directions.
export enum ScrollDirection {
    UP = 'up',
    DOWN = 'down',
    LEFT = 'left',
    RIGHT = 'right',
    VERTICAL = 'vertical',
    HORIZONTAL = 'horizontal',
    ANY = 'any'
}

// Enum for host element scopes.
export enum HostElementScope {
    // Detect only scroll events on the children of hostElement (excluding hostElement itself).
    CHILDREN = 'children',
    // Detect only scroll events on the ancestors of hostElement (excluding hostElement itself).
    PARENTS = 'parents',
    // Detect scroll events on hostElement, its children, and its ancestors.
    BOTH = 'both',
    // Detect only scroll events on hostElement.
    SELF = 'self'
}

export interface ScrollOptions {
    // Specifies the expected direction to trigger the callback:
    direction: ScrollDirection;
    // Threshold in pixels to trigger the callback.
    threshold: number;
    // Optional host element to exclude scroll events from its children.
    hostElement?: Element;
    // Optional host scope filtering out events outside boundaries.
    hostElementScope?: HostElementScope;
}

@Injectable({
    providedIn: 'root'
})
export class ScrollDetectorService {
    private scrollSub: Subscription | null = null;
    // Stores the last scroll positions per element
    private lastPositions: WeakMap<Element, { vertical: number; horizontal: number }> = new WeakMap();

    constructor(private ngZone: NgZone) {}

    // Returns the scroll positions for the given element.
    private getElementScrollPositions(element: Element): { vertical: number; horizontal: number } {
        if (element instanceof HTMLElement) {
            return {
                vertical: element.scrollTop,
                horizontal: element.scrollLeft
            };
        }
        // Fallback: use window positions
        return {
            vertical: window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0,
            horizontal: window.pageXOffset || document.documentElement.scrollLeft || document.body.scrollLeft || 0
        };
    }

    /**
     * Starts listening to scroll events on the document in capture mode.
     * @param options - Scroll options.
     * @param callback - Callback function to be called when the threshold of scroll direction is exceeded.
     */
    startListening(
        options: ScrollOptions,
        callback: (dir: ScrollDirection, scrollTop: number, scrollLeft: number) => void
    ): void {
        // Stop any previous subscription if it exists
        this.stopListening();

        const { direction, threshold, hostElement, hostElementScope } = options;
        this.ngZone.runOutsideAngular(() => {
            this.scrollSub = fromEvent(document, 'scroll', { capture: true })
                .pipe(
                    // Limit callback frequency to improve performance. Throttle events to every 50ms
                    throttleTime(50)
                )
                .subscribe((event: Event) => {
                    const target = event.target as Element;

                    // if a hostElement is provided, filter events based on hostElementScope
                    if (hostElement && !this.shouldProcessEventForHost(hostElement, hostElementScope, target)) {
                        return;
                    }

                    const current = this.getElementScrollPositions(target);

                    // Retrieve or initialize last positions for this element.
                    let last = this.lastPositions.get(target);
                    if (!last) {
                        last = { vertical: current.vertical, horizontal: current.horizontal };
                        this.lastPositions.set(target, last);
                    }

                    const { accepted, detected } = this.isMovementAccepted(last, current, threshold, direction);
                    if (accepted && detected) {
                        // Update the last positions for the target element.
                        last.vertical = current.vertical;
                        last.horizontal = current.horizontal;
                        this.ngZone.run(() => callback(detected, current.vertical, current.horizontal));
                    }
                });
        });
    }

    /**
     * Stops listening to scroll events.
     */
    stopListening(): void {
        if (this.scrollSub) {
            this.scrollSub.unsubscribe();
            this.scrollSub = null;
        }
    }

    /**
     * This function is used to decide whether a scroll event, coming from a target element,
     * should be handled according to the specified host element and its scope.
     * @param hostElement The reference host element used for filtering scroll events.
     * @param hostElementScope The scope defining which related scroll events to process.
     * @param target The element that triggered the scroll event.
     * @returns True if the event should be processed; false otherwise.
     */
    private shouldProcessEventForHost(
        hostElement: Element,
        hostElementScope: HostElementScope | undefined,
        target: Element
    ): boolean {
        if (hostElementScope === HostElementScope.CHILDREN) {
            // process only if the target is a descendant of hostElement (excluding hostElement itself)
            return target !== hostElement && hostElement.contains(target);
        } else if (hostElementScope === HostElementScope.PARENTS) {
            // process only if the target is an ancestor of hostElement (excluding hostElement itself)
            return target !== hostElement && target.contains(hostElement);
        } else if (hostElementScope === HostElementScope.BOTH) {
            // process if the target is hostElement, a descendant, or an ancestor of hostElement
            return hostElement.contains(target) || target.contains(hostElement);
        } else if (hostElementScope === HostElementScope.SELF) {
            // process only if the target is exactly hostElement
            return target === hostElement;
        } else {
            // default behavior: exclude events from hostElement and its descendants
            return !hostElement.contains(target);
        }
    }


    /**
     * This function is used to decide whether the scroll movement is significant enough
     * and in the correct direction to trigger the callback.
     * @param last The last recorded scroll positions.
     * @param current The current scroll positions.
     * @param threshold The threshold value in pixels.
     * @param configDirection The scroll direction configured for triggering the callback.
     * @returns An object with a boolean indicating if the movement is accepted and the detected scroll direction.
     */
    private isMovementAccepted(
        last: { vertical: number; horizontal: number },
        current: { vertical: number; horizontal: number },
        threshold: number,
        configDirection: ScrollDirection
    ): { accepted: boolean; detected: ScrollDirection | null } {
        const diffV = Math.abs(current.vertical - last.vertical);
        const diffH = Math.abs(current.horizontal - last.horizontal);
        let detected: ScrollDirection | null = null;

        // Determine dominant movement based on the larger difference
        if (diffV >= threshold && diffV >= diffH) {
            detected = current.vertical > last.vertical ? ScrollDirection.DOWN : ScrollDirection.UP;
        } else if (diffH >= threshold) {
            detected = current.horizontal > last.horizontal ? ScrollDirection.RIGHT : ScrollDirection.LEFT;
        }

        if (detected) {
            // Check if the detected direction matches the configuration.
            if (
                configDirection === ScrollDirection.ANY ||
                (configDirection === ScrollDirection.VERTICAL && (detected === ScrollDirection.UP || detected === ScrollDirection.DOWN)) ||
                (configDirection === ScrollDirection.HORIZONTAL && (detected === ScrollDirection.LEFT || detected === ScrollDirection.RIGHT)) ||
                configDirection === detected
            ) {
                return { accepted: true, detected: detected };
            }
        }

        return { accepted: false, detected: null };
    }


}
